import { Bot, Hand, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn, formatEtherWithDecimals } from "@/lib/utils";
import { formatUSDT } from "@/lib/mockData";
import type { RunningOrderView } from "@/components/aiTrading/orderViewTypes";
import { getRiskProfileI18nKey } from "@/config/common";

const C = {
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#3b82f6",
};

const DIALOG_H5 = "max-w-sm w-full mx-auto sm:max-w-sm";

const COIN_LOGOS: Record<string, string> = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.svg",
};

function CoinLogo({ coin, size = 18 }: { coin: string; size?: number }) {
  const src = COIN_LOGOS[coin];
  if (!src) {
    return (
      <span className="font-bold" style={{ fontSize: size * 0.7 }}>
        {coin[0]}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={coin}
      style={{ width: size, height: size }}
      className="rounded-full"
    />
  );
}

function DirBadge({ dir }: { dir: string }) {
  const { t } = useTranslation();
  const isLong = dir === "多";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold mono-num"
      style={{
        background: isLong ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
        color: isLong ? C.green : C.red,
        border: `1px solid ${isLong ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
      }}
    >
      {isLong ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {isLong ? t("aiTradingPage.badgeLong") : t("aiTradingPage.badgeShort")}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const isAuto = source === "auto";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{
        background: isAuto ? "rgba(34,197,94,0.10)" : "rgba(59,130,246,0.10)",
        color: isAuto ? C.green : C.blue,
        border: `1px solid ${isAuto ? "rgba(34,197,94,0.20)" : "rgba(59,130,246,0.20)"}`,
      }}
    >
      {isAuto ? <Bot size={9} /> : <Hand size={9} />}
      {isAuto ? t("aiTradingPage.badgeAuto") : t("aiTradingPage.badgeManual")}
    </span>
  );
}

function PnlDisplay({
  value,
  size = "sm",
}: {
  value: number;
  size?: "sm" | "lg";
}) {
  const isPos = value >= 0;
  const textSize = size === "lg" ? "text-lg" : "text-sm";
  return (
    <span
      className={`mono-num font-bold ${textSize}`}
      style={{ color: isPos ? C.green : C.red }}
    >
      {isPos ? "+" : "-"}${formatUSDT(Math.abs(value))}
    </span>
  );
}

export default function OrderDetailDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: RunningOrderView | null;
}) {
  const { t } = useTranslation();
  if (!order) return null;
  const isProfit = order.pnl >= 0;
  const tradeFeeWei = BigInt(order.tradeFeeTotalUsdtAmount);
  const signalFeeWei = BigInt(order.signalFeeTotalUsdtAmount);
  const totalFeeWei = tradeFeeWei + signalFeeWei;
  const rid = Number(order.riskId ?? 0);
  const riskI18nKey = getRiskProfileI18nKey(rid);
  const riskName =
    riskI18nKey === "aiTradingPage.riskProfileUnknown"
      ? t(riskI18nKey, { id: rid })
      : t(riskI18nKey);

  const detailRows: [string, string][] = [
    [t("aiTradingPage.detailFieldOrderId"), order.id],
    [t("aiTradingPage.detailFieldOpenTime"), order.openTime],
    [t("aiTradingPage.detailFieldStrategyType"), riskName],
    [t("aiTradingPage.ordersMargin"), `$${formatUSDT(order.margin)}`],
    [t("aiTradingPage.detailFieldLeverage"), `${order.leverage}x`],
    [t("aiTradingPage.detailFieldTpSl"), t("aiTradingPage.detailTpSlAiValue")],
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn(DIALOG_H5, "p-0 overflow-hidden max-h-[85vh]")}
        style={{
          background: "#0d0f14",
          border: `1px solid ${isProfit ? "rgba(34,197,94,0.20)" : "rgba(239,68,68,0.20)"}`,
          color: C.text,
        }}
      >
        <DialogTitle className="sr-only">
          {t("aiTradingPage.detailOrderTitle", {
            pair: `${order.coin}/USDT`,
          })}
        </DialogTitle>
        <div
          className="relative px-5 pt-5 pb-4"
          style={{
            background: isProfit
              ? "linear-gradient(180deg, rgba(34,197,94,0.08) 0%, transparent 100%)"
              : "linear-gradient(180deg, rgba(239,68,68,0.08) 0%, transparent 100%)",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <CoinLogo coin={order.coin} size={20} />
            <span className="font-bold text-base" style={{ color: C.text }}>
              {order.coin}/USDT
            </span>
            <DirBadge dir={order.type} />
            {/* <SourceBadge source={order.orderSource} /> */}
          </div>
          <div className="text-center py-2">
            {/*
            <div className="text-[10px] mb-1" style={{ color: C.dim }}>
              当前盈亏
            </div>
            <PnlDisplay value={order.pnlDisplay} size="lg" />
            */}
            <div
              className="text-[10px] px-2.5 py-1 rounded-full inline-block"
              style={{
                background: "rgba(34,197,94,0.08)",
                color: C.green,
                border: "1px solid rgba(34,197,94,0.2)",
              }}
            >
              {t("aiTradingPage.detailRunningBullet")}
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 space-y-3">
          <div
            className="rounded-xl p-3.5 space-y-2 text-xs"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            {detailRows.map(([k, v]) => (
              <div key={k as string} className="flex justify-between">
                <span style={{ color: C.dim }}>{k}</span>
                <span
                  className="mono-num font-medium"
                  style={{ color: C.text }}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>

          <div
            className="rounded-xl p-3.5 space-y-2 text-xs"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div
              className="text-xs font-semibold mb-1"
              style={{ color: C.dim }}
            >
              {t("aiTradingPage.detailFeeSection")}
            </div>
            <div className="flex justify-between">
              <span style={{ color: C.dim }}>
                {t("aiTradingPage.feeTrade012")}
              </span>
              <span className="mono-num" style={{ color: C.muted }}>
                ${formatEtherWithDecimals(tradeFeeWei, 2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: C.dim }}>
                {t("aiTradingPage.feeCompute015")}
              </span>
              <span className="mono-num" style={{ color: C.muted }}>
                ${formatEtherWithDecimals(signalFeeWei, 2)}
              </span>
            </div>
            <div
              className="flex justify-between pt-1.5"
              style={{ borderTop: `1px solid ${C.border}` }}
            >
              <span className="font-medium" style={{ color: C.muted }}>
                {t("aiTradingPage.feeSubtotal")}
              </span>
              <span className="mono-num font-medium" style={{ color: C.text }}>
                ${formatEtherWithDecimals(totalFeeWei, 2)}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
